import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { hasHistoryContext } from '../../services/api.js';
import { notificationsApi } from '../../services/notificationsApi.js';
import { readOnline } from '../../utils/connectivity.js';
import {
  compterNonLues, critiquesASignaler, curseurMax, delaiReconnexion, fusionnerNotifications, trierNotifications,
} from '../agents/agentsModel.js';

// ════════════════════════════════════════════════════════════════════
// Centre de notifications et centre des agents (P1, contrat §5 et §11) : état partagé par
// la cloche et le bouton « Agents » de l'en-tête, leurs tiroirs et la fiche d'un agent.
//   - useNotifications(user) : liste, non-lues et flux temps réel des notifications ;
//   - useTiroirsCentre() : tiroir ouvert (notifications ou agents) et retour du focus ;
//   - CentreProvider / useCentre() : les deux, plus la question posée dans la conversation
//     (« Créer depuis un modèle », « Modifier », forme de VisualAskContext : {ask, busy}).
// Sans JSX (createElement) : le module est chargé tel quel par les tests node.
// ════════════════════════════════════════════════════════════════════

// Relecture de secours quand le flux est tombé (contrat §5)
export const RELEVE_MS = 60 * 1000;
// Notifications relues à chaque lecture de la liste (le compte des non-lues vient du serveur)
export const LIMITE_LISTE = 50;
// Sous ce point de rupture (Header.css), la cloche reste seule dans l'en-tête et son tiroir
// porte un onglet « Agents » à la place du bouton dédié
export const CENTRE_COMPACT_QUERY = '(max-width: 480px)';

// Web Push (§6bis) : l'iframe de la plateforme ne peut pas demander la permission ; une
// petite fenêtre de premier niveau, sur l'adresse propre de l'assistant, le fait. Chemin
// routé dans App.jsx comme /admin/qualite ; nom fixe : un second clic réutilise la fenêtre
export const CHEMIN_ACTIVATION = '/notifications/activer';
export const FENETRE_ACTIVATION = Object.freeze({
  url: CHEMIN_ACTIVATION,
  nom: 'digitrans-notifications',
  options: 'width=480,height=640',
});
// Messages échangés par postMessage entre l'iframe et la fenêtre d'activation, toujours
// adressés à l'origine de l'assistant et vérifiés (origine et source) à la réception :
// pret (fenêtre → iframe : elle écoute), jeton (iframe → fenêtre), echec (iframe → fenêtre :
// jeton refusé), actif et refus (fenêtre → iframe : issue de l'activation)
export const MESSAGES_PUSH = Object.freeze({
  pret: 'digitrans-push:pret',
  jeton: 'digitrans-push:jeton',
  echec: 'digitrans-push:echec',
  actif: 'digitrans-push:actif',
  refus: 'digitrans-push:refus',
});

// Refus définitif : route absente (serveur sans agents), contexte refusé, ou réponse qui n'est
// pas un flux. Flux refusé : plus de reconnexion, la liste relue toutes les 60 s suffit ; liste
// refusée : plus rien (mode indisponible). Jusqu'à la prochaine relecture demandée (retour sur
// l'onglet, retour du réseau, ouverture du tiroir)
const estDefinitif = (erreur) => [401, 403, 404, 405].includes(erreur?.status) || erreur?.code === 'FLUX_INVALIDE';

const entier = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
};

const ETAT_VIDE = { items: [], nonLues: 0 };

/**
 * Notifications de l'utilisateur, tenues à jour en temps réel.
 * - Liste : GET /api/notifications au démarrage, à l'ouverture du flux (intervalle entre la
 *   dernière lecture et l'ouverture), au retour sur l'onglet ou du réseau, puis toutes les
 *   60 s tant que le flux est tombé.
 * - Flux : GET /api/notifications/flux lu par fetch et readSse (jamais EventSource, jamais le
 *   hash dans l'URL), repris après le dernier curseur reçu ; fermé quand l'onglet est caché ou
 *   hors ligne ; reconnexion avec un délai croissant (delaiReconnexion).
 * - Alerte critique arrivée depuis peu : onCritique(notification), une seule fois par alerte.
 * mode : 'inactif' (sans contexte complet), 'connexion', 'direct' (flux ouvert), 'releve'
 * (flux tombé, relecture de secours), 'pause' (onglet caché ou hors ligne), 'indisponible'
 * (route absente ou contexte refusé).
 * releveMs et delai : réglables pour les tests.
 * @returns {{actif, items, nonLues, mode, marquerLue, toutMarquerLu, accuser, relire}}
 */
export function useNotifications(user, { onCritique = null, releveMs = RELEVE_MS, delai = delaiReconnexion } = {}) {
  const actif = hasHistoryContext(user);
  const [etat, setEtat] = useState(ETAT_VIDE);
  const [mode, setMode] = useState(actif ? 'connexion' : 'inactif');

  // État lu par les rappels asynchrones (flux, réponses) sans dépendre du rendu
  const etatRef = useRef(ETAT_VIDE);
  const userRef = useRef(user);
  userRef.current = user;
  const onCritiqueRef = useRef(onCritique);
  onCritiqueRef.current = onCritique;
  const curseurRef = useRef(null);
  const signaleesRef = useRef(new Set());
  const premiereLectureRef = useRef(false);
  const lectureRef = useRef(null);
  const monteRef = useRef(true);
  // Commandes du cycle de vie du flux (effet ci-dessous) : relance, arrêt définitif
  const commandesRef = useRef(null);

  const publier = useCallback((items, nonLues) => {
    etatRef.current = { items, nonLues: Math.max(0, nonLues) };
    if (monteRef.current) setEtat(etatRef.current);
  }, []);

  /**
   * Fusionne des notifications reçues (liste, flux) ; serveurNonLues : total du serveur
   * (liste) ; sinon les nouvelles non lues s'ajoutent au compte. signaler : toast des
   * alertes critiques nouvelles (jamais pour la toute première lecture : c'est un arriéré).
   */
  const appliquer = useCallback((arrivees, { serveurNonLues = null, signaler = true } = {}) => {
    const avant = etatRef.current;
    const { items, nouvelles } = fusionnerNotifications(avant.items, arrivees);
    const nonLues = serveurNonLues === null
      ? avant.nonLues + nouvelles.filter((n) => !n.lue_le).length
      : Math.max(serveurNonLues, compterNonLues(items));
    curseurRef.current = curseurMax(items, curseurRef.current);
    publier(items, nonLues);
    if (!signaler) {
      nouvelles.forEach((n) => signaleesRef.current.add(n.id));
      return;
    }
    for (const n of critiquesASignaler(nouvelles)) {
      if (signaleesRef.current.has(n.id)) continue;
      signaleesRef.current.add(n.id);
      onCritiqueRef.current?.(n);
    }
  }, [publier]);

  // Lecture de la liste ; une lecture déjà en cours est partagée (ouverture du tiroir et
  // retour du réseau au même moment, par exemple)
  const lire = useCallback(() => {
    if (!hasHistoryContext(userRef.current)) return Promise.resolve(null);
    if (lectureRef.current) return lectureRef.current;
    const demande = notificationsApi
      .liste(userRef.current, { limite: LIMITE_LISTE })
      .then((data) => {
        if (!monteRef.current) return null;
        const liste = Array.isArray(data?.notifications) ? data.notifications : [];
        appliquer(liste, { serveurNonLues: entier(data?.non_lues), signaler: premiereLectureRef.current });
        premiereLectureRef.current = true;
        return data;
      })
      .catch((erreur) => {
        if (estDefinitif(erreur)) commandesRef.current?.indisponible();
        return null;
      })
      .finally(() => {
        lectureRef.current = null;
      });
    lectureRef.current = demande;
    return demande;
  }, [appliquer]);

  useEffect(() => {
    monteRef.current = true;
    return () => {
      monteRef.current = false;
    };
  }, []);

  // ── Cycle de vie du flux : ouverture, reconnexion, relève, pause ──
  useEffect(() => {
    if (!actif) {
      setMode('inactif');
      return undefined;
    }
    let arrete = false;
    let enPause = false;
    let indispo = false;
    let controleur = null;
    let minuterie = null;
    let releve = null;
    let tentative = 0;

    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
    const enLigne = () => readOnline(typeof navigator === 'undefined' ? null : navigator);

    const arreterReleve = () => {
      if (releve) clearInterval(releve);
      releve = null;
    };
    const demarrerReleve = () => {
      if (!releve) releve = setInterval(() => lire(), releveMs);
    };
    const arreterFlux = () => {
      if (minuterie) clearTimeout(minuterie);
      minuterie = null;
      if (controleur) {
        const c = controleur;
        controleur = null;
        c.abort();
      }
    };

    const fin = (c, erreur) => {
      if (controleur !== c) return; // flux remplacé, mis en pause ou arrêté entre-temps
      controleur = null;
      if (arrete || enPause || indispo) return;
      if (erreur && estDefinitif(erreur)) {
        // Flux absent (serveur sans flux) : la liste relue toutes les 60 s suffit, si elle répond
        setMode('releve');
        demarrerReleve();
        return;
      }
      // Fin propre d'un flux ouvert (serveur redémarré) : nouvel essai aussitôt, sans bandeau ;
      // échec ou coupure : relecture de secours jusqu'à la reprise, essais de plus en plus espacés
      if (erreur || tentative > 0) {
        setMode('releve');
        demarrerReleve();
      } else {
        setMode('connexion');
      }
      const attente = delai(tentative);
      tentative += 1;
      minuterie = setTimeout(() => {
        minuterie = null;
        ouvrir();
      }, attente);
    };

    function ouvrir() {
      if (arrete || enPause || indispo || controleur) return;
      const c = new AbortController();
      controleur = c;
      if (tentative === 0) setMode('connexion');
      notificationsApi
        .flux(userRef.current, {
          apres: curseurRef.current,
          signal: c.signal,
          onOuvert: () => {
            if (controleur !== c) return;
            tentative = 0;
            arreterReleve();
            setMode('direct');
            // Intervalle entre la dernière lecture et l'ouverture : relu une fois
            lire();
          },
          onEvent: (type, data) => {
            if (controleur === c && type === 'notification') appliquer([data]);
          },
        })
        .then(() => fin(c, null), (erreur) => fin(c, erreur));
    }

    const pause = () => {
      if (enPause) return;
      enPause = true;
      arreterFlux();
      arreterReleve();
      setMode('pause');
    };
    // Retour sur l'onglet ou du réseau : vaut relecture demandée (un refus précédent est retenté)
    const reprendre = () => {
      if (!enPause) return;
      enPause = false;
      indispo = false;
      tentative = 0;
      lire();
      ouvrir();
    };
    const surChangement = () => (visible() && enLigne() ? reprendre() : pause());

    commandesRef.current = {
      // Relecture demandée (retour du réseau, ouverture du tiroir) : flux rouvert sans attendre
      relancer: () => {
        if (arrete || enPause) return;
        indispo = false;
        tentative = 0;
        if (minuterie) {
          clearTimeout(minuterie);
          minuterie = null;
        }
        ouvrir();
      },
      indisponible: () => {
        if (arrete) return;
        indispo = true;
        arreterFlux();
        arreterReleve();
        setMode('indisponible');
      },
    };

    document.addEventListener('visibilitychange', surChangement);
    window.addEventListener('online', surChangement);
    window.addEventListener('offline', surChangement);
    if (visible() && enLigne()) {
      lire();
      ouvrir();
    } else {
      enPause = true;
      setMode('pause');
    }

    return () => {
      arrete = true;
      commandesRef.current = null;
      document.removeEventListener('visibilitychange', surChangement);
      window.removeEventListener('online', surChangement);
      window.removeEventListener('offline', surChangement);
      arreterFlux();
      arreterReleve();
    };
  }, [actif, user, releveMs, delai, lire, appliquer]);

  // Relecture demandée par l'interface : liste relue, flux rouvert s'il était tombé
  const relire = useCallback(() => {
    const demande = lire();
    commandesRef.current?.relancer();
    return demande;
  }, [lire]);

  // Modification locale (optimiste) : liste retriée, compte ajusté
  const modifier = useCallback((transformer, nonLues) => {
    const items = trierNotifications(etatRef.current.items.map(transformer));
    publier(items, nonLues);
  }, [publier]);

  // Compte du serveur après une action, jamais en dessous des non-lues affichées
  const recompter = useCallback((data) => {
    const serveur = entier(data?.non_lues);
    if (serveur !== null) publier(etatRef.current.items, Math.max(serveur, compterNonLues(etatRef.current.items)));
  }, [publier]);

  const marquerLue = useCallback(async (id) => {
    const cible = etatRef.current.items.find((n) => n.id === id);
    if (!cible || cible.lue_le) return;
    const lue = new Date().toISOString();
    modifier((n) => (n.id === id ? { ...n, lue_le: lue } : n), etatRef.current.nonLues - 1);
    try {
      recompter(await notificationsApi.marquerLue(id, userRef.current));
    } catch (erreur) {
      modifier((n) => (n.id === id ? { ...n, lue_le: null } : n), etatRef.current.nonLues + 1);
      throw erreur;
    }
  }, [modifier, recompter]);

  const toutMarquerLu = useCallback(async () => {
    const avant = etatRef.current;
    const lue = new Date().toISOString();
    const nonLuesAvant = new Set(avant.items.filter((n) => !n.lue_le).map((n) => n.id));
    modifier((n) => (n.lue_le ? n : { ...n, lue_le: lue }), 0);
    try {
      recompter(await notificationsApi.toutMarquerLu(userRef.current));
    } catch (erreur) {
      modifier((n) => (nonLuesAvant.has(n.id) ? { ...n, lue_le: null } : n), avant.nonLues);
      throw erreur;
    }
  }, [modifier, recompter]);

  // « Pris en compte » : l'alerte est accusée (une seule fois côté serveur), la notification lue
  const accuser = useCallback(async (notification) => {
    if (!notification?.alerte_id) return;
    await notificationsApi.accuserAlerte(notification.alerte_id, userRef.current);
    modifier((n) => (n.alerte_id === notification.alerte_id ? { ...n, accusee: true } : n), etatRef.current.nonLues);
    const courante = etatRef.current.items.find((n) => n.id === notification.id);
    if (courante && !courante.lue_le) await marquerLue(notification.id).catch(() => {});
  }, [modifier, marquerLue]);

  return useMemo(
    () => ({ actif, items: etat.items, nonLues: etat.nonLues, mode, marquerLue, toutMarquerLu, accuser, relire }),
    [actif, etat, mode, marquerLue, toutMarquerLu, accuser, relire]
  );
}

/**
 * Tiroir ouvert dans le centre ('notifications', 'agents' ou null) et retour du focus au
 * bouton de l'en-tête qui l'a ouvert (modèle du panier de rapport, ReportBasketContext).
 * onglet : onglet demandé à l'ouverture (par exemple 'agents' dans le tiroir de la cloche).
 * fermer({rendreFocus: false}) : avant d'ouvrir la fiche d'un agent dans le panneau, qui
 * prend le bouton de l'en-tête comme déclencheur (declencheur()).
 */
export function useTiroirsCentre() {
  const [tiroir, setTiroir] = useState(null);
  const [onglet, setOnglet] = useState(null);
  const declencheurRef = useRef(null);

  const ouvrir = useCallback((nom, declencheur = null, ongletDemande = null) => {
    declencheurRef.current = declencheur ?? (typeof document !== 'undefined' ? document.activeElement : null);
    setOnglet(ongletDemande);
    setTiroir(nom);
  }, []);

  const fermer = useCallback(({ rendreFocus = true } = {}) => {
    setTiroir(null);
    const declencheur = declencheurRef.current;
    if (!rendreFocus) return;
    requestAnimationFrame(() => {
      if (declencheur?.isConnected) declencheur.focus({ preventScroll: true });
    });
  }, []);

  const declencheur = useCallback(() => declencheurRef.current, []);

  return useMemo(() => ({ tiroir, onglet, ouvrir, fermer, declencheur }), [tiroir, onglet, ouvrir, fermer, declencheur]);
}

const SANS_CENTRE = {
  actif: false,
  items: [],
  nonLues: 0,
  mode: 'inactif',
  marquerLue: async () => {},
  toutMarquerLu: async () => {},
  accuser: async () => {},
  relire: async () => null,
  tiroir: null,
  onglet: null,
  ouvrir: () => {},
  fermer: () => {},
  declencheur: () => null,
  demande: { ask: null, busy: false },
  agentsActifs: 0,
};

export const CentreContext = createContext(SANS_CENTRE);

/**
 * Fournisseur du centre (App) : notifications (useNotifications), tiroirs (useTiroirsCentre),
 * demande : {ask, busy}, la question posée dans la conversation, reprise par les tiroirs
 * et la fiche d'un agent à travers VisualAskContext, et agentsActifs, la pastille du bouton
 * « Agents » (useCompteAgents).
 */
export function CentreProvider({ notifications, tiroirs, demande, agentsActifs = 0, children }) {
  const value = useMemo(() => ({ ...notifications, ...tiroirs, demande, agentsActifs }), [notifications, tiroirs, demande, agentsActifs]);
  return createElement(CentreContext.Provider, { value }, children);
}

export const useCentre = () => useContext(CentreContext);
